import { ChevronDown, ListChecks, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { usePermissionCatalog } from '../api'
import { permissionDisplayLabel, toggleMany } from '../permissions'

type Props = {
  value: string[]
  onChange: (codes: string[]) => void
  selected: string | null
  onSelect: (code: string) => void
  readOnly?: boolean
}

export function PermissionMatrix({ value, onChange, selected, onSelect, readOnly }: Props) {
  const { t } = useTranslation()
  const catalog = usePermissionCatalog().data
  const [q, setQ] = useState('')
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())
  const granted = useMemo(() => new Set(value), [value])

  const groups = useMemo(() => {
    const term = q.trim().toLocaleLowerCase('tr')
    const source = catalog?.groups.length
      ? catalog.groups
      : (catalog?.modules ?? []).map((m) => ({
          key: m.key,
          title: m.title || m.key,
          description: m.description || '',
          sortOrder: m.sortOrder ?? 0,
          permissions: m.resources.flatMap((r) =>
            r.actions.map((action) => {
              const code = r.codes?.[action] ?? `${m.key}.${r.key}.${action}`
            return {
              id: catalog?.idByCode[code] ?? code,
              code,
              title: catalog?.titleByCode[code] || '',
              description: r.descriptions?.[action] || catalog?.descriptionByCode[code] || '',
              module: m.key,
            }
            }),
          ),
        }))

    return source
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter(
          (p) =>
            !term ||
            group.title.toLocaleLowerCase('tr').includes(term) ||
            group.description.toLocaleLowerCase('tr').includes(term) ||
            p.code.toLocaleLowerCase('tr').includes(term) ||
            (p.title || '').toLocaleLowerCase('tr').includes(term) ||
            p.description.toLocaleLowerCase('tr').includes(term),
        ),
      }))
      .filter((g) => g.permissions.length > 0)
  }, [catalog, q])

  const visibleCodes = groups.flatMap((g) => g.permissions.map((p) => p.code))
  const allVisibleOn = visibleCodes.length > 0 && visibleCodes.every((c) => granted.has(c))

  const toggleCollapsed = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <div className="overflow-hidden rounded-2xl border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b p-3">
        <div className="relative basis-full sm:min-w-48 sm:flex-1 sm:basis-auto">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('access.matrix.search')} aria-label={t('access.matrix.search')} className="pl-8" />
        </div>
        {!readOnly ? (
          <Button size="sm" variant="outline" className="h-9 shrink-0" disabled={visibleCodes.length === 0} onClick={() => onChange(toggleMany(value, visibleCodes))}>
            <ListChecks />
            {allVisibleOn ? t('access.matrix.clearGroup') : t('access.matrix.selectGroup')}
          </Button>
        ) : null}
      </div>

      <div className="divide-y">
        {groups.map((group) => {
          const isCollapsed = collapsed.has(group.key) && !q
          const codes = group.permissions.map((p) => p.code)
          const count = codes.filter((c) => granted.has(c)).length
          return (
            <section key={group.key}>
              <button
                type="button"
                aria-expanded={!isCollapsed}
                onClick={() => toggleCollapsed(group.key)}
                className="flex w-full items-start gap-2 px-3 py-3 text-left hover:bg-muted/40"
              >
                <ChevronDown className={cn('mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform', isCollapsed && '-rotate-90')} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="font-semibold">{group.title}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {count}/{codes.length}
                    </span>
                  </span>
                  {group.description ? <span className="mt-0.5 block text-xs text-muted-foreground">{group.description}</span> : null}
                </span>
              </button>
              {!isCollapsed ? (
                <ul className="border-t bg-card">
                  {group.permissions.map((p) => {
                    const on = granted.has(p.code)
                    const active = selected === p.code
                    const label = p.title || p.description || permissionDisplayLabel(p.code, catalog, p.code)
                    return (
                      <li key={p.code} className={cn('border-b last:border-b-0', active && 'bg-brand-blue/[0.06]')}>
                        <label className="flex cursor-pointer items-start gap-3 px-4 py-2.5 pl-10">
                          <input
                            type="checkbox"
                            className="mt-0.5 size-4 shrink-0 accent-brand-blue disabled:cursor-default"
                            checked={on}
                            disabled={readOnly}
                            onChange={() => {
                              if (!readOnly) onChange(toggleMany(value, [p.code]))
                              onSelect(p.code)
                            }}
                            onFocus={() => onSelect(p.code)}
                          />
                          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onSelect(p.code)}>
                            <span className="block text-sm font-medium">{label}</span>
                            {p.description && p.description !== label ? (
                              <span className="mt-0.5 block text-xs text-muted-foreground">{p.description}</span>
                            ) : null}
                            <span className="mt-0.5 block font-mono text-[11px] text-muted-foreground">{p.code}</span>
                          </button>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              ) : null}
            </section>
          )
        })}
      </div>
      {groups.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">{t('access.matrix.empty')}</p> : null}
    </div>
  )
}
